/* eslint-disable @typescript-eslint/naming-convention */
import {
    defineEvents, SHARED_EVENTS
} from '@3plearning/mx-teacher-analytics';

import { TrackSpotlightClosedProps, TrackSpotlightShownProps } from './tracking.types';

const COURSETYPE = 'coursetype';
const ASSIGNMENT = 'assignment';
const FILTERS = 'filters';
const GRID_INTERACTIONS = 'grid-interactions';
const UI = 'ui';
const VIEWRESULTS = 'viewresults';

export const appEvents = defineEvents({
    'page.view': {
        group: SHARED_EVENTS['page.view'].group,
        props: {} as { path: string }
    },
    'page.time-on-page': SHARED_EVENTS['page.time-on-page'],
    'section.error': SHARED_EVENTS['section.error'],
    'coursetype.view': {
        group: COURSETYPE,
        props: {} as { path: string }
    },
    /**
     * A teacher leaving A&R for another portal module. Deliberately shares the
     * prop vocabulary of the portal's `teacher.console.navigation.click`
     * (`module_id`, `module_name`, `click_type`) so a single PostHog action can
     * union the two and break down by entry point.
     *
     * The destination cannot count this for itself - the portal swaps modules on
     * a `MODULE_CHANGE` message that carries no referrer.
     */
    'module.clicked': {
        group: COURSETYPE,
        props: {} as {
            module_id: number
            module_name: string
            click_type: string
        }
    },
    'assignment.flow-started': {
        group: ASSIGNMENT,
        props: {} as {
            type: 'assign' | 'reassign'
            count: number
            group_id?: string
            scope: 'group' | 'class'
        }
    },
    'assignment.confirmation': {
        group: ASSIGNMENT,
        props: {} as {
            type: 'assign' | 'reassign' | 'unassign' | 'modify'
            count?: number
            is_assigned_activities_modal?: boolean
            course_id?: string
            group_id?: string
            scope: 'group' | 'class'
            is_assign_to_all_students?: boolean
        }
    },
    'grid.checkbox-click': {
        group: GRID_INTERACTIONS,
        props: {} as {
            type: 'student' | 'topic' | 'activity'
            is_checked: boolean
            topic_id?: string
            activity_id?: string
            student_user_profile_id?: number | string
        }
    },
    'grid.cell-click': {
        group: GRID_INTERACTIONS,
        props: {} as {
            type: 'topic' | 'activity'
            topic_id: string
            activity_id?: string | null
            activity_type?: number | null
            student_user_profile_id: number | string
            is_selected: boolean
        }
    },
    'student.scroll': {
        group: ASSIGNMENT,
        props: {} as Record<string, never>
    },
    'filter.class-changed': {
        group: FILTERS,
        props: {} as { value: string }
    },
    'filter.group-changed': {
        group: FILTERS,
        props: {} as { value: string; classroom_id: string }
    },
    'filter.dynamic': {
        group: FILTERS,
        props: {} as { type: 'gridViewOptions' | 'iconView' }
    },
    'filter.challenges-course': {
        group: FILTERS,
        props: {} as { type: 'levelFilter' | 'dokFilter' | 'playlistFilter'; value?: unknown }
    },
    'filter.activities-course': {
        group: FILTERS,
        props: {} as { type: 'topicFilter' | 'complexityFilter' }
    },
    'ui.scroll-to-top': {
        group: UI,
        props: {} as Record<string, never>
    },
    'ui.view-key': {
        group: UI,
        props: {} as Record<string, never>
    },
    'ui.spotlight-shown': {
        group: UI,
        props: {} as TrackSpotlightShownProps
    },
    'ui.spotlight-closed': {
        group: UI,
        props: {} as TrackSpotlightClosedProps
    },
    'viewresults.button': {
        group: VIEWRESULTS,
        props: {} as Record<string, never>
    },
    'viewresults.modal-close-icon': {
        group: VIEWRESULTS,
        props: {} as Record<string, never>
    },
    'viewresults.modal-close-button': {
        group: VIEWRESULTS,
        props: {} as Record<string, never>
    },
    'viewresults.modal-student-selector': {
        group: VIEWRESULTS,
        props: {} as Record<string, never>
    },
    'viewresults.modal-previous-activity': {
        group: VIEWRESULTS,
        props: {} as Record<string, never>
    },
    'viewresults.modal-next-activity': {
        group: VIEWRESULTS,
        props: {} as Record<string, never>
    }
});

export type AppEvents = typeof appEvents;
